import React, { useMemo } from "react";
import { Dropdown } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Select from "react-select";
import {
  IconAdjustments,
  IconCalendarEvent,
  IconChevronLeft,
  IconChevronRight,
} from "@tabler/icons-react";
import { useUser } from "contexts/v1/masterData/UserContext";
import "../attendance-recap-page.css";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    borderColor: "#dfe3e8",
    borderRadius: "4px",
    boxShadow: "none",
    "&:hover": { borderColor: "#bfc6cf" },
  }),
  menu: (base) => ({ ...base, zIndex: 1035 }),
  menuPortal: (base) => ({ ...base, zIndex: 1035 }),
};

const AttendanceHubToolbar = ({
  startDate,
  endDate,
  departmentId,
  userId,
  onDateChange,
  onFilterChange,
  onPreviousPeriod,
  onNextPeriod,
  onToday,
  actionControls,
  extraControls,
  adjustControls,
}) => {
  const { users } = useUser();

  const departmentOptions = useMemo(() => {
    const departments = new Map();
    (users.data || []).forEach((user) => {
      if (user.department?.id && user.department?.name) {
        departments.set(user.department.id, user.department.name);
      }
    });
    return [{ value: "", label: "All Departments" }, ...Array.from(departments, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))];
  }, [users.data]);

  const employeeOptions = useMemo(() => {
    const filteredUsers = (users.data || []).filter((user) => !departmentId || String(user.department?.id) === String(departmentId));
    return [{ value: "", label: "All Employees" }, ...filteredUsers.map((user) => ({ value: user.id, label: user.name }))];
  }, [users.data, departmentId]);

  const selectedDepartment = departmentOptions.find((option) => String(option.value) === String(departmentId)) || departmentOptions[0];
  const selectedEmployee = employeeOptions.find((option) => String(option.value) === String(userId)) || employeeOptions[0];

  return (
    <div className="card border-0 shadow-sm mb-4 sticky-top attendance-hub-toolbar">
      <div className="card-body p-3">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <button className="btn btn-sm btn-light border px-2 text-muted" onClick={onPreviousPeriod} title="Previous Period">
                <IconChevronLeft size={16} />
              </button>
              <div className="input-icon attendance-hub-datepicker">
                <span className="input-icon-addon"><IconCalendarEvent size={16} /></span>
                <DatePicker selected={startDate} onChange={(date) => onDateChange(date, "startDate")} className="form-control form-control-sm border-bottom cursor-pointer" dateFormat="dd MMM yyyy" portalId="root" locale="id" popperClassName="attendance-hub-datepicker-popper" />
              </div>
              <span className="text-muted small fw-bold">TO</span>
              <div className="input-icon attendance-hub-datepicker">
                <span className="input-icon-addon"><IconCalendarEvent size={16} /></span>
                <DatePicker selected={endDate} onChange={(date) => onDateChange(date, "endDate")} className="form-control form-control-sm border-bottom cursor-pointer" dateFormat="dd MMM yyyy" portalId="root" locale="id" popperClassName="attendance-hub-datepicker-popper" />
              </div>
              <button className="btn btn-sm btn-light border px-2 text-muted" onClick={onNextPeriod} title="Next Period">
                <IconChevronRight size={16} />
              </button>
            </div>

            <button className="btn btn-sm btn-light border fw-bold text-primary px-3 rounded" onClick={onToday}>
              Go to Today
            </button>

            {adjustControls && (
              <Dropdown autoClose="outside">
                <Dropdown.Toggle variant="light" size="sm" className="border fw-bold text-secondary px-3 rounded">
                  <IconAdjustments size={14} className="me-1" /> Adjust Size
                </Dropdown.Toggle>
                <Dropdown.Menu className="attendance-hub-adjust-menu">
                  {adjustControls}
                </Dropdown.Menu>
              </Dropdown>
            )}

            {extraControls}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
            <div className="attendance-hub-filter">
              <Select
                classNamePrefix="react-select"
                options={departmentOptions}
                value={selectedDepartment}
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
                onChange={(option) => onFilterChange({ departmentId: option?.value || "", userId: "" })}
              />
            </div>
            <div className="attendance-hub-filter">
              <Select
                classNamePrefix="react-select"
                options={employeeOptions}
                value={selectedEmployee}
                isClearable
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={selectStyles}
                onChange={(option) => onFilterChange({ userId: option?.value || "" })}
              />
            </div>
            {actionControls}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceHubToolbar;
